import type { AppLocale } from '@/i18n/locales'
import type { SpeechAvailability, SpeechLanguage } from './speech-types'

export function speechLanguage(locale: AppLocale): SpeechLanguage {
  return locale.startsWith('zh') ? 'zh-CN' : locale === 'ja' ? 'ja-JP' : locale === 'ko' ? 'ko-KR' : 'en-US'
}
export function speechLocale(locale: AppLocale): AppLocale { return locale.startsWith('zh') ? 'zh-CN' : locale }
export function voiceId(voice: SpeechSynthesisVoice): string { return `${voice.voiceURI}|${voice.lang}|${voice.name}` }
export function chineseVoiceKind(voice: SpeechSynthesisVoice): 'mandarin' | 'taiwan' | 'hongKong' | 'macau' | 'cantonese' | 'chinese' {
  const lang = voice.lang.toLowerCase().replaceAll('_', '-')
  if (/(^|-)hk(-|$)/.test(lang)) return 'hongKong'
  if (/(^|-)mo(-|$)/.test(lang)) return 'macau'
  if (/^yue(-|$)/.test(lang) || /\byue\b|cantonese|粤|粵|廣東話|广东话/i.test(voice.name)) return 'cantonese'
  if (/(^|-)tw(-|$)/.test(lang)) return 'taiwan'
  if (/^zh-(cn|sg|hans)(-|$)/.test(lang) || /^cmn(-|$)/.test(lang)) return 'mandarin'
  return 'chinese'
}
export function matchingVoices(voices: SpeechSynthesisVoice[], language: SpeechLanguage): SpeechSynthesisVoice[] {
  return voices.filter(voice => {
    const lang = voice.lang.toLowerCase().replaceAll('_', '-')
    if (language === 'zh-CN') return /^(zh|cmn|yue)(-|$)/.test(lang)
    return lang.split('-')[0] === language.split('-')[0]
  }).sort((a, b) => (language === 'zh-CN' ? Number(chineseVoiceKind(b) === 'mandarin') - Number(chineseVoiceKind(a) === 'mandarin') : 0) || Number(b.lang.toLowerCase().replaceAll('_', '-') === language.toLowerCase()) - Number(a.lang.toLowerCase().replaceAll('_', '-') === language.toLowerCase()) || Number(b.localService) - Number(a.localService) || voiceId(a).localeCompare(voiceId(b)))
}

/** Voice inventories commonly arrive after the first render. Never use an unfiltered default. */
export class SystemVoices {
  voices: SpeechSynthesisVoice[] = []
  private selected = new Map<SpeechLanguage, string>()
  private subscribers = new Set<() => void>()
  private synth: SpeechSynthesis | undefined
  private state: Exclude<SpeechAvailability, 'voice-missing'> = 'unchecked'
  private waiters = new Set<() => void>()
  private poll: ReturnType<typeof setInterval> | undefined
  private timeout: ReturnType<typeof setTimeout> | undefined
  private disposed = false
  constructor() {
    this.synth = typeof speechSynthesis === 'undefined' ? undefined : speechSynthesis
    this.refresh()
    this.synth?.addEventListener?.('voiceschanged', this.refresh)
  }
  get supported(): boolean {
    return !!this.synth && typeof SpeechSynthesisUtterance === 'function'
      && ['getVoices', 'speak', 'cancel'].every(method => typeof this.synth![method as keyof SpeechSynthesis] === 'function')
  }
  private notify(): void { this.subscribers.forEach(fn => fn()) }
  refresh = (): boolean => {
    if (this.disposed) return false
    let readable = false
    if (!this.supported) { this.voices = []; this.state = 'unsupported' }
    else {
      try {
        this.voices = this.synth!.getVoices()
        readable = true
        this.state = this.voices.length ? 'ready' : this.waiters.size ? 'checking' : this.state === 'unchecked' ? 'unchecked' : 'voices-unavailable'
      } catch { this.voices = []; this.state = 'voices-unavailable' }
    }
    this.notify()
    if (this.waiters.size && this.state !== 'checking') this.finishWait()
    return readable
  }
  subscribe(fn: () => void): () => void { this.subscribers.add(fn); return () => this.subscribers.delete(fn) }
  availability(locale: AppLocale, preferred?: string): SpeechAvailability {
    if (!this.supported) return 'unsupported'
    return this.state === 'ready' && !this.resolve(locale, preferred) ? 'voice-missing' : this.state
  }
  options(locale: AppLocale): SpeechSynthesisVoice[] { return matchingVoices(this.voices, speechLanguage(locale)) }
  private resolve(locale: AppLocale, preferred?: string): SpeechSynthesisVoice | undefined {
    const language = speechLanguage(locale), options = this.options(locale)
    const explicit = options.find(item => voiceId(item) === preferred)
    if (explicit) return explicit
    const automatic = language === 'zh-CN' ? options.filter(item => chineseVoiceKind(item) === 'mandarin') : options
    return automatic.find(item => voiceId(item) === this.selected.get(language)) ?? automatic[0]
  }
  select(locale: AppLocale, preferred?: string): SpeechSynthesisVoice | undefined {
    const voice = this.resolve(locale, preferred)
    if (voice && voiceId(voice) !== preferred) this.selected.set(speechLanguage(locale), voiceId(voice))
    return voice
  }
  wait(signal: AbortSignal): Promise<void> {
    if (this.disposed || signal.aborted) return Promise.resolve()
    if (!this.waiters.size) {
      // A retry always reads the browser again, including when voiceschanged was missed.
      this.state = 'unchecked'
      const readable = this.refresh()
      if (!this.supported || this.voices.length || !readable) return Promise.resolve()
    }
    if (this.disposed || signal.aborted) return Promise.resolve()
    return new Promise<void>(resolve => {
      const done = () => {
        this.waiters.delete(done); signal.removeEventListener('abort', done)
        if (!this.waiters.size) {
          this.clearTimers()
          if (this.state === 'checking') { this.state = 'unchecked'; this.notify() }
        }
        resolve()
      }
      this.waiters.add(done)
      signal.addEventListener('abort', done, { once: true })
      if (this.poll !== undefined) return
      this.poll = setInterval(this.refresh, 250)
      this.timeout = setTimeout(() => {
        this.refresh()
        if (this.waiters.size) { this.state = 'voices-unavailable'; this.notify(); this.finishWait() }
      }, 1500)
      this.state = 'checking'; this.notify()
    })
  }
  private clearTimers(): void {
    clearInterval(this.poll); clearTimeout(this.timeout)
    this.poll = undefined; this.timeout = undefined
  }
  private finishWait(): void { for (const done of [...this.waiters]) done() }
  dispose(): void {
    this.disposed = true
    this.synth?.removeEventListener?.('voiceschanged', this.refresh)
    this.subscribers.clear(); this.finishWait(); this.clearTimers()
  }
}
